import { type ReactNode, useEffect, useRef } from 'react';

import { STR } from '../../../../i18n';
import { rowActionStyle } from '../../../../ui/styles';

/**
 * A collapsible section card — the demo's `foldCard`, drawn with the same header the
 * Applications screen's "Launcher tile" card uses: title, a quiet aside, and an
 * Expand / Collapse text action on the right. The open state is the caller's, so "Expand
 * all" / "Collapse all" can drive every section at once.
 */
export function FoldCard({
  title,
  count,
  aside,
  open,
  onToggle,
  children,
}: {
  title: string;
  count?: number;
  aside?: string;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  return (
    <section
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 12,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '11px 18px',
          background: 'var(--surface-2)',
          ...(open ? { borderBottom: '1px solid var(--border)' } : {}),
        }}
      >
        <h3 style={{ margin: 0, fontSize: 13.5, fontWeight: 650 }}>
          {title}
          {count === undefined ? null : (
            <span
              className="tx-subtle"
              style={{ fontWeight: 600, marginLeft: 6, fontVariantNumeric: 'tabular-nums' }}
            >
              {count}
            </span>
          )}
        </h3>
        {aside === undefined ? null : (
          <span className="tx-subtle" style={{ fontSize: 12, fontWeight: 500 }}>
            {aside}
          </span>
        )}
        <button
          type="button"
          className="tbtn"
          aria-expanded={open}
          style={{ ...rowActionStyle, marginLeft: 'auto' }}
          onClick={onToggle}
        >
          {open ? STR.access.catalog.collapse : STR.access.catalog.expand}
        </button>
      </div>
      {open ? children : null}
    </section>
  );
}

/**
 * The catalog row's side drawer: the demo opens a row beside the list rather than over it.
 * Same contract as `Modal` — Escape closes, focus moves in on open and back on close, every
 * colour a token — anchored to the right edge instead of centred.
 */
export function Drawer({
  label,
  onClose,
  children,
}: {
  label: string;
  onClose: () => void;
  children: ReactNode;
}) {
  const panelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const previous = document.activeElement;
    panelRef.current?.focus();
    function onKeyDown(event: KeyboardEvent) {
      // A dialog opened from inside the drawer handles its own Escape first; only close
      // the drawer when nothing above it is open.
      if (event.key === 'Escape' && document.querySelector('[role="dialog"]') === null) {
        onClose();
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      if (previous instanceof HTMLElement) previous.focus();
    };
  }, [onClose]);

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 70 }}>
      <div
        className="backdrop"
        style={{ position: 'absolute', inset: 0 }}
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        ref={panelRef}
        role="complementary"
        aria-label={label}
        tabIndex={-1}
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          width: 'min(620px,100%)',
          overflowY: 'auto',
          background: 'var(--bg)',
          borderLeft: '1px solid var(--border-strong)',
          boxShadow: 'var(--elev-modal)',
          outline: 'none',
          padding: '24px 26px 60px',
          boxSizing: 'border-box',
        }}
      >
        {children}
      </div>
    </div>
  );
}
